import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import type { MgaBondedLine, MgaPeriod } from '../useData';
import { MgaReportRoot } from './MgaReportRoot';
import { ReportHeader } from './ReportHeader';
import { formatReportMoney, reportNum, reportRound2 } from './reportMeta';

export type BondedByCrewReportProps = {
  period: MgaPeriod;
  bonded: MgaBondedLine[];
  companyLogoSrc?: string | null;
  companyLogoAlt?: string;
};

export const BondedByCrewReport: React.FC<BondedByCrewReportProps> = ({
  period,
  bonded,
  companyLogoSrc = null,
  companyLogoAlt,
}) => {
  const groups = useMemo(() => {
    const expenses = bonded.filter((l) => l.section === 'EXPENSE');
    const byCrew = new Map<string, { crew: string; lines: MgaBondedLine[]; total: number }>();
    for (const l of expenses) {
      const key = l.crewLine?.crewName || 'Unassigned';
      const cur = byCrew.get(key) || { crew: key, lines: [], total: 0 };
      cur.lines.push(l);
      cur.total = reportRound2(cur.total + reportNum(l.amount));
      byCrew.set(key, cur);
    }
    return Array.from(byCrew.values()).sort((a, b) => a.crew.localeCompare(b.crew));
  }, [bonded]);

  const grand = useMemo(
    () => reportRound2(groups.reduce((s, g) => s + g.total, 0)),
    [groups],
  );

  return (
    <MgaReportRoot>
      <ReportHeader
        period={period}
        title="Bonded by crew (Stock out)"
        companyLogoSrc={companyLogoSrc}
        companyLogoAlt={companyLogoAlt}
      />

      <section className="mga-report-section">
        <h2>Summary</h2>
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Crew</th>
              <th className="mga-report-num">Lines</th>
              <th className="mga-report-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={3} className="mga-report-muted">
                  No bonded issues
                </td>
              </tr>
            ) : (
              groups.map((g) => (
                <tr key={g.crew}>
                  <td>{g.crew}</td>
                  <td className="mga-report-num">{g.lines.length}</td>
                  <td className="mga-report-num">{formatReportMoney(g.total)}</td>
                </tr>
              ))
            )}
          </tbody>
          {groups.length > 0 ? (
            <tfoot>
              <tr>
                <td className="mga-report-emph">Total</td>
                <td className="mga-report-num mga-report-emph">
                  {groups.reduce((s, g) => s + g.lines.length, 0)}
                </td>
                <td className="mga-report-num mga-report-emph">{formatReportMoney(grand)}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </section>

      <section className="mga-report-section mga-report-break">
        <h2>Issue detail</h2>
        {groups.length === 0 ? (
          <p className="mga-report-muted">No bonded issues</p>
        ) : (
          groups.map((g, gi) => (
            <div
              key={g.crew}
              className={gi > 0 ? 'mga-report-crew-block' : undefined}
            >
              <h3 className="mga-report-subhead">
                {g.crew}{' '}
                <span className="mga-report-muted">
                  ({g.lines.length} · {formatReportMoney(g.total)})
                </span>
              </h3>
              <table className="mga-report-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th className="mga-report-num">Unit</th>
                    <th className="mga-report-num">Qty</th>
                    <th className="mga-report-num">Amount</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((l) => (
                    <tr key={l.id}>
                      <td>{l.entryDate ? formatDateGlobal(l.entryDate) : '—'}</td>
                      <td>{l.description}</td>
                      <td className="mga-report-num">{formatReportMoney(l.unitPrice)}</td>
                      <td className="mga-report-num">{formatReportMoney(l.qty)}</td>
                      <td className="mga-report-num">{formatReportMoney(l.amount)}</td>
                      <td>{l.remarks || '—'}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4} className="mga-report-emph">
                      Subtotal
                    </td>
                    <td className="mga-report-num mga-report-emph">
                      {formatReportMoney(g.total)}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          ))
        )}
      </section>
    </MgaReportRoot>
  );
};

export default BondedByCrewReport;
